/**
 * Product image gallery — previous / next through a product's photos.
 *
 * Works on any element carrying data-gallery, so both the standard and the
 * lifestyle product page use the same code. See snippets/product-gallery.liquid.
 *
 * Notes
 *  - The variant-switching scripts on both pages set the image's src directly.
 *    Rather than hooking into them, this watches the src attribute and keeps its
 *    own index in step. That way the two never have to know about each other.
 *  - Arrows wrap around, so the last image steps to the first.
 *  - Keyboard arrows work when the gallery has focus; touch swipe works on
 *    mobile, where the arrows are small.
 */
(function () {
  'use strict';

  function initGallery(root) {
    var img = root.querySelector('[data-gallery-image]');
    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    var currentEl = root.querySelector('[data-gallery-current]');
    var dataEl = root.querySelector('[data-gallery-images]');

    if (!img || !dataEl || !prevBtn || !nextBtn) return;

    var images = [];
    try {
      images = JSON.parse(dataEl.textContent) || [];
    } catch (e) {
      console.warn('[gallery] Could not read the image list:', e);
      return;
    }
    if (images.length < 2) return;

    function srcOf(i) { return images[i] && images[i].src; }

    function indexOfSrc(src) {
      for (var i = 0; i < images.length; i++) {
        if (images[i] && images[i].src === src) return i;
      }
      return -1;
    }

    var index = Math.max(0, indexOfSrc(img.getAttribute('src')));
    var syncing = false;
    var loaded = {};   /* index -> true once fetched, so it is not fetched twice */

    function paint() {
      if (currentEl) currentEl.textContent = String(index + 1);
    }

    /*
     * Fetch an image without displaying it. Uses the same srcset as the visible
     * image, so the browser downloads the size it would actually use and the
     * later swap comes from cache.
     */
    function preload(i) {
      if (i < 0 || i >= images.length || loaded[i]) return null;
      var pre = new Image();
      if (images[i].srcset) {
        pre.srcset = images[i].srcset;
        pre.sizes = img.sizes || '(min-width: 900px) 50vw, 100vw';
      }
      pre.src = srcOf(i);
      pre.decoding = 'async';
      pre.onload = function () { loaded[i] = true; };
      return pre;
    }

    /* Keep the two the shopper is one click away from ready. */
    function preloadNeighbours() {
      preload((index + 1) % images.length);
      preload((index - 1 + images.length) % images.length);
    }

    function show(nextIndex) {
      index = (nextIndex + images.length) % images.length;
      var entry = images[index];
      if (!entry) return;

      paint();

      /*
       * Fade out, swap once the file is decoded, fade back in. is-loading adds
       * a subtle pulse so a slow connection does not look like a broken image —
       * these are large photographs.
       */
      root.classList.add('is-loading');
      img.style.opacity = '0';

      var pre = new Image();
      if (entry.srcset) {
        pre.srcset = entry.srcset;
        pre.sizes = img.sizes || '(min-width: 900px) 50vw, 100vw';
      }

      function reveal() {
        syncing = true;
        if (entry.srcset) img.srcset = entry.srcset;
        img.src = entry.src;
        syncing = false;
        img.style.opacity = '1';
        root.classList.remove('is-loading');
        loaded[index] = true;
        preloadNeighbours();
      }

      pre.onload = reveal;
      pre.onerror = function () {
        img.style.opacity = '1';
        root.classList.remove('is-loading');
      };
      pre.src = entry.src;

      /* Already cached: onload may not fire in some browsers. */
      if (pre.complete) reveal();
    }

    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      show(index - 1);
    });

    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      show(index + 1);
    });

    /* Keyboard, once the shopper has interacted with the gallery. */
    root.setAttribute('tabindex', root.getAttribute('tabindex') || '0');
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1); }
    });

    /* Swipe, for mobile where the arrows are deliberately small. */
    var touchStartX = null;
    root.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].clientX;
    }, { passive: true });

    root.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var delta = e.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) < 45) return;      /* ignore taps and tiny drags */
      show(delta < 0 ? index + 1 : index - 1);
    }, { passive: true });

    /*
     * A variant change swaps the src from outside this module. Follow it so the
     * counter and the arrows continue from the image actually on screen.
     */
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (syncing) return;
        var found = indexOfSrc(img.getAttribute('src'));
        if (found > -1 && found !== index) {
          index = found;
          paint();
        }
      }).observe(img, { attributes: true, attributeFilter: ['src'] });
    }

    paint();

    /*
     * Warm the next and previous images once the browser is idle, so the first
     * arrow click is instant without pulling every photo up front. Deferred so
     * it never competes with the first image, which is what the shopper is
     * waiting on.
     */
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preloadNeighbours, { timeout: 3000 });
    } else {
      window.setTimeout(preloadNeighbours, 1200);
    }
  }

  function init() {
    document.querySelectorAll('[data-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
